import { type ComponentChildren, createContext } from "preact";
import { useContext, useMemo, useState } from "preact/hooks";
import { AuthView } from "../components/auth/AuthView";
import { Modal } from "../components/Modal";

type AuthModalContextType = {
  /** Asks an anonymous visitor to sign in; `title` and `message` are the modal's wording. */
  showAuthModal: (title?: string, message?: string) => void;
};

type ModalState = { open: boolean; title?: string; message?: string };

const AuthModalContext = createContext<AuthModalContextType | undefined>(undefined);

/**
 * The sign-in modal for gated actions on public pages: the `/auth` sign-in body over the current page. After sign-in the
 * visitor lands back on the page they were on (a Google round trip returns through `/auth` with the same `redirect`).
 */
export const AuthModalProvider = ({ children }: { children: ComponentChildren }) => {
  const [state, setState] = useState<ModalState>({ open: false });
  const value = useMemo<AuthModalContextType>(() => ({ showAuthModal: (title, message) => setState({ open: true, title, message }) }), []);

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      <Modal isOpen={state.open} onClose={() => setState({ open: false })} testId="auth-modal">
        <AuthView title={state.title} subtitle={state.message} redirect={state.open ? `${window.location.pathname}${window.location.search}` : undefined} />
      </Modal>
    </AuthModalContext.Provider>
  );
};

export const useAuthModal = (): AuthModalContextType => {
  const context = useContext(AuthModalContext);
  if (context === undefined) {
    throw new Error("useAuthModal must be used within an AuthModalProvider");
  }
  return context;
};
